import React, {useEffect, useMemo, useState} from 'react';
import type {TableColumnsType, UploadProps} from 'antd';
import {Button, Col, DatePicker, Divider, Drawer, Form, Input, message, Row, Select, Space, Table} from 'antd';
import {ArrowDownOutlined, ArrowUpOutlined, CopyOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {PickupTaskEndPointType, PickupTaskStartPointType, PickupTaskStatus} from '../types/PickupTaskType';
import TransferTaskModal from './TransferTaskModal';
import AddAppointmentModal from './AddAppointmentModal';
import PickupAppointmentDrawer from '../../appointment/components/PickupAppointmentDrawer';
import {useMutation, useQuery} from '@apollo/client';
import {GET_PICKUP_TASK_DETAIL} from '../graphql/queries/getPickupTaskDetail';
import {GET_PICKUP_TASK_DETAIL_ITEMS, PickupTaskDetailItem} from '../graphql/queries/getPickupTaskDetailItemList';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import {CREATE_PICKUP_TASK} from '../graphql/mutations/createPickupTask';
import {UPDATE_PICKUP_TASK} from '../graphql/mutations/updatePickupTask';
import {UPDATE_PICKUP_TASK_STATUS} from '../graphql/mutations/updatePickupTaskStatus';
import {usePickupData} from '../contexts/PickupDataContext';
import {SWAP_PICKUP_TASK_APPOINTMENTS} from '../graphql/mutations/swapPickupTaskAppointments';
import {TRANSFER_PICKUP_TASK_APPOINTMENT} from '../graphql/mutations/transferPickupTaskAppointment';
import {UPDATE_UNLOAD_RECORD_PHOTOS} from '../graphql/mutations/updateUnloadRecordPhotos';
import PointSelector from './PointSelector';
import {GET_VEHICLES_BY_WAREHOUSE_ID, GetVehiclesByWarehouseIdResponse} from '../../../../hooks/useVehicleOptions';
import {GET_PICKUP_POINT_OPTIONS, PickupPointOptionsData} from '../graphql/queries/getPickupPointOptions';
import {GET_HUB_LIST, GetHubListResponse, HubType} from '../../appointment/graphql/queries/getHubList';
import CooperationWarehouseFormDrawer from '../../cooperation-warehouse/components/CooperationWarehouseFormDrawer';
import { usePickupSiteV2Gray } from '../../../../hooks/usePickupSiteV2Gray';
import { useStationViewer } from '../../address-review-pool/hooks/useStationViewer';
import {
    GET_COOPERATION_WAREHOUSE_BY_ID,
    FindCooperationWarehouseByIdResponse
} from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';

// 导入单位选项配置
import {packageUnitFilterOptions} from '../../appointment/types/PickupAppointmentType';
import { useTranslation } from 'react-i18next';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import ImageUploader from '../../../../components/ImageUploader';
// 定义各状态下允许编辑的字段映射
const EDITABLE_FIELDS_BY_STATUS: Record<PickupTaskStatus, string[]> = {
    [PickupTaskStatus.DRAFT]: [
        'taskDate', 'accountId', 'vehicleId', 'vehicleTypeId', 'routeId',
        'startPoint', 'endPoint',
        'expDepartureTime', 'expCompleteTime', 'notes',
        'estimatedDistanceKm', 'estimatedDurationMinutes'
    ],
    [PickupTaskStatus.PUBLISHED]: [
        'accountId', 'vehicleId', 'vehicleTypeId', 'routeId',
        'endPoint',
        'expDepartureTime', 'expCompleteTime', 'notes'
    ],
    [PickupTaskStatus.IN_PROGRESS]: [
        'endPoint'
    ],
    [PickupTaskStatus.COMPLETED]: [],
    [PickupTaskStatus.CANCELLED]: []
};

// 单位映射工具函数 - 与PickupTaskCreateDrawer保持一致
const formatUnit = (unit: string | null | undefined): string => {
    if (!unit) return '-';

    // 使用与PickupTaskCreateDrawer相同的逻辑
    return packageUnitFilterOptions.find(option => option.value === unit)?.label || unit;
};


// 定义状态枚举（这些是UI展示需要的）
enum PickupTaskNodeStatus {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    END_POINT = 'END_POINT'
}

// 定义点位值的接口
interface PointValue {
    type: number;
    value: string;
}

// Origin/Destination选择组件
interface OriginDestinationSelectProps {
    value?: string | PointValue;
    onChange?: (value: PointValue) => void;
    placeholder?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    pointType?: number;
    warehouseId?: string;
}

// 导出组件，使其可以在其他文件中使用
export const OriginDestinationSelect: React.FC<OriginDestinationSelectProps> = ({
                                                                                    value,
                                                                                    onChange,
                                                                                    placeholder,
                                                                                    style,
                                                                                    disabled,
                                                                                    pointType,
                                                                                    warehouseId
                                                                                }) => {
    // 处理值的格式转换
    const getSelectValue = (): string | undefined => {
        if (!value) return undefined;

        // 如果已经是字符串，可能是JSON字符串或普通字符串
        if (typeof value === 'string') {
            // 尝试解析为JSON，看是否是序列化的对象
            try {
                const parsed = JSON.parse(value);
                // 如果解析成功且包含type和value属性，返回原始字符串
                if (parsed && typeof parsed === 'object' && 'type' in parsed && 'value' in parsed) {
                    return value; // 返回原始JSON字符串
                }
            } catch (e) {
                // 解析失败，说明是普通字符串，直接返回
                return value;
            }
        }

        // 如果是对象，需要将其序列化为JSON字符串
        if (typeof value === 'object' && 'type' in value && 'value' in value) {
            return JSON.stringify(value);
        }

        // 其他情况返回undefined
        return undefined;
    };

    // 处理onChange事件
    const handleChange = (selectedValue: string): void => {
        if (!onChange || !pointType) return;

        try {
            // 尝试解析选中的值，它应该是一个JSON字符串
            const parsedValue = JSON.parse(selectedValue);

            // 如果解析成功，传递解析后的对象
            if (parsedValue && typeof parsedValue === 'object' && 'type' in parsedValue && 'value' in parsedValue) {
                onChange(parsedValue as PointValue);
                return;
            }
        } catch (e) {
            // 解析失败，说明不是JSON字符串
            console.log('Failed to parse selected value:', e);
        }

        // 如果解析失败或不是预期的格式，创建新的PointValue对象
        onChange({
            type: pointType,
            value: selectedValue
        });
    };

    const getOptions = (): Array<{ label: string, value: string }> => {
        switch (pointType) {
            case PickupTaskStartPointType.HUB:
            case PickupTaskEndPointType.HUB:
                // 现在由PointSelector组件处理Hub数据
                return [];
            case PickupTaskStartPointType.RALLY_POINT:
            case PickupTaskEndPointType.RALLY_POINT:
                // 中继点数据暂时不存在，返回空数组
                return [];
            case PickupTaskStartPointType.FIRST_APPOINTMENT:
                return [
                    {
                        label: 'Use First Appointment Address',
                        // 值是一个包含type和value的对象，但需要序列化为字符串
                        value: JSON.stringify({
                            type: pointType,
                            value: 'first_appointment'
                        })
                    }
                ];
            default:
                return [];
        }
    };

    const isLoading = false; // 加载状态现在由PointSelector组件处理

    return (
        <Select
            value={getSelectValue()}
            onChange={handleChange}
            placeholder={placeholder || 'Please select'}
            style={style}
            disabled={disabled || !pointType}
            loading={isLoading}
            showSearch
            optionFilterProp="label"
            options={getOptions()}
            notFoundContent={pointType ? 'No data' : 'Please select type first'}
        />
    );
};

interface PickupTaskDetailDrawerProps {
    visible: boolean;
    onClose: () => void;
    taskId: string | null;
    onSave?: (data: any) => void;
}

// 移除mockTaskDetail及相关mock数据

const PickupTaskDetailDrawer: React.FC<PickupTaskDetailDrawerProps> = ({
                                                                           visible,
                                                                           onClose,
                                                                           taskId,
                                                                           onSave
                                                                       }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const isEdit = !!taskId;
    const {t, i18n} = useTranslation();
    // 所有useQuery都放在顶层，始终执行
    const {data: detailData, loading: detailLoading, refetch: refetchDetail} = useQuery(GET_PICKUP_TASK_DETAIL, {
        variables: {taskId},
        skip: !isEdit || !visible,
        fetchPolicy: 'network-only'
    });
    const {
        data: appointmentsData,
        loading: appointmentsLoading,
        error: appointmentsError,
        refetch: refetchAppointments
    } = useQuery(GET_PICKUP_TASK_DETAIL_ITEMS, {
        variables: {taskId},
        skip: !isEdit || !visible,
        fetchPolicy: 'network-only'
    });

    // Hub数据现在由PointSelector组件处理
    const {drivers, driversLoading, warehouseId} = usePickupData();

    // 揽收站点 2.0 灰度：灰度 hub 下点站点走 2.0 编辑弹窗（建联状态锁死不可改），非灰度沿用老 1.0 组件
    const { express2Enabled } = usePickupSiteV2Gray();
    const isV2 = express2Enabled(warehouseId ? Number(warehouseId) : undefined);
    const { openEditStation, viewerDrawer } = useStationViewer({
        onSaved: () => { refetchDetail(); refetchAppointments(); },
    });

    // 过滤司机数据：只显示当前任务匹配的司机和启用状态的司机
    const filteredDrivers = useMemo(() => {
        if (!drivers || drivers.length === 0) return [];

        const currentTaskAccountId = detailData?.getPickupTaskDetail?.accountId;

        return drivers.filter((driver: any) => {
            // 如果当前任务有司机，优先显示该司机
            if (currentTaskAccountId && driver.accountId === currentTaskAccountId) {
                return true;
            }
            // 否则只显示启用状态的司机
            return driver.status === 1;
        });
    }, [drivers, detailData?.getPickupTaskDetail?.accountId]);

    // 直接使用useQuery获取完整的车辆数据
    const {data: vehiclesData, loading: vehiclesLoading} = useQuery<GetVehiclesByWarehouseIdResponse>(
        GET_VEHICLES_BY_WAREHOUSE_ID,
        {
            variables: {warehouseId: warehouseId ? Number(warehouseId) : undefined},
            skip: !warehouseId,
        }
    );

    // 获取车型数据
    const { vehicleTypes: vehicleModelsData, loading: vehicleModelsLoading } = useVehicleTypes();

    // 获取点位选项数据，用于终点行显示
    const {data: pointOptionsData} = useQuery(GET_PICKUP_POINT_OPTIONS, {
        variables: {
            input: {
                warehouseId: warehouseId || '',
                taskId: taskId || undefined
            }
        },
        skip: !warehouseId || !isEdit,
    });

    // 获取hub列表数据，用于获取hub时区信息
    const {data: hubListData} = useQuery<GetHubListResponse>(GET_HUB_LIST, {
        skip: !isEdit,
    });

    // 添加选中的车型ID状态
    const [selectedVehicleModelId, setSelectedVehicleModelId] = useState<string | null>(null);

    // 表格操作 loading 状态
    const [tableLoading, setTableLoading] = useState(false);
    const [lastClickTime, setLastClickTime] = useState(0);
    const CLICK_THROTTLE_MS = 800;

    // 获取时间约束信息（简化版，不使用时区转换）
    const getTimeConstraints = () => {
        const taskDate = form.getFieldValue('taskDate');
        const endPoint = form.getFieldValue('endPoint');

        if (!taskDate) return null;

        let pickupDeadlineTime: string | undefined;
        let selectedDestinationHub: HubType | null = null;

        // 通过hubListData获取hub信息
        if (endPoint?.type === PickupTaskEndPointType.HUB && endPoint?.value && hubListData?.hubList) {
            selectedDestinationHub = hubListData.hubList.find(
                (hub: HubType) => hub.id === endPoint.value
            ) || null;
            if (selectedDestinationHub) {
                pickupDeadlineTime = selectedDestinationHub.pickupDeadlineTime;
            }
        }

        const taskDateLocal = taskDate;

        // 直接基于taskDate创建开始和结束时间（不使用时区转换）
        const hubStart = dayjs(`${taskDateLocal.format('YYYY-MM-DD')} 00:00:00`);
        const hubEnd = pickupDeadlineTime
            ? (() => {
                const timeParts = pickupDeadlineTime.split(':').map(Number);
                const hour = timeParts[0] || 23;
                const minute = timeParts[1] || 59;
                const second = timeParts[2] || 59;
                return dayjs(`${taskDateLocal.format('YYYY-MM-DD')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`);
            })()
            : dayjs(`${taskDateLocal.format('YYYY-MM-DD')} 23:59:59`);

        return {
            hubStart,
            hubEnd,
            pickupDeadlineTime
        };
    };

    // 处理车型变化
    const handleVehicleModelChange = (vehicleModelId: string) => {
        setSelectedVehicleModelId(vehicleModelId);
        // 清空车辆选择
        form.setFieldsValue({vehicleId: undefined});
    };

    // 处理司机变化
    const handleDriverChange = (value: string) => {
        // 查找选中司机的车辆信息
        const selectedDriver = filteredDrivers.find((driver: any) => driver.accountId === value);

        if (selectedDriver?.vehicleId) {
            // 如果司机有关联车辆，自动选择该车辆
            const vehicle = vehiclesData?.vehicles.find(
                (v: any) => v.id === selectedDriver.vehicleId
            );

            if (vehicle) {
                // 自动选择车辆
                form.setFieldsValue({
                    vehicleId: vehicle.id
                });

                // 自动选择车辆型号
                const vtId = vehicle.vehicleTypeId;
                if (vtId) {
                    const vehicleTypeIdStr = vtId.toString();
                    form.setFieldsValue({
                        vehicleTypeId: vehicleTypeIdStr
                    });
                    setSelectedVehicleModelId(vehicleTypeIdStr);
                }
            }
        } else {
            // 如果司机没有关联车辆，清空车辆型号选择
            setSelectedVehicleModelId(null);
        }
    };

    // 复制司机文本
    const handleCopyDriver = () => {
        const driverName = detailData?.getPickupTaskDetail?.driverName ||
            filteredDrivers.find(
                (driver) => driver.accountId === form.getFieldValue('accountId')
            )?.driverName ||
            '';
        if (driverName) {
            navigator.clipboard.writeText(driverName).then(() => {
                message.success(t('common.messages.copied'));
            }).catch(() => {
                message.error(t('common.messages.copyFailed'));
            });
        }
    };

    // 根据选中的车型过滤车辆选项
    const getFilteredVehicles = () => {
        if (!selectedVehicleModelId || !vehiclesData?.vehicles) return [];
        return vehiclesData.vehicles
            .filter(vehicle => vehicle.status === 1) // 只显示可用状态的车辆
            .filter(vehicle => {
                const id = vehicle.vehicleTypeId;
                return id?.toString() === selectedVehicleModelId;
            })
            .map(vehicle => ({
                value: vehicle.id,
                label: `${vehicle.vehicleNumber}-${vehicle.licensePlate}`
            }));
    };

    // 弹窗状态
    const [transferModalVisible, setTransferModalVisible] = useState(false);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [selectedStopId, setSelectedStopId] = useState<string>('');
    const [appointmentDrawerVisible, setAppointmentDrawerVisible] = useState(false);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<number | undefined>(undefined);
    const [cooperationWarehouseDrawerVisible, setCooperationWarehouseDrawerVisible] = useState(false);
    const [selectedCooperationWarehouse, setSelectedCooperationWarehouse] = useState<any>(null);
    const [cooperationWarehouseDrawerMode, setCooperationWarehouseDrawerMode] = useState<'create' | 'edit' | 'view'>('view');
    const [updateUnloadRecordPhotos] = useMutation(UPDATE_UNLOAD_RECORD_PHOTOS);

    const { refetch: refetchCooperationWarehouseDetail } = useQuery<FindCooperationWarehouseByIdResponse>(
        GET_COOPERATION_WAREHOUSE_BY_ID,
        {
            variables: { id: null },
            skip: true,
        }
    );

    const showPickupSiteDetail = async (cooperationWarehouseId?: number | null) => {
        if (!cooperationWarehouseId) {
            message.warning('No cooperation warehouse ID available');
            return;
        }
        // 灰度 hub：走 2.0 站点编辑弹窗（可编辑，建联状态锁死），保存后刷新任务详情/预约单/统计
        if (isV2 && warehouseId) {
            openEditStation(cooperationWarehouseId, Number(warehouseId));
            return;
        }
        setSelectedCooperationWarehouse(null);
        setCooperationWarehouseDrawerMode('view');
        setCooperationWarehouseDrawerVisible(true);
        try {
            const result = await refetchCooperationWarehouseDetail({ id: cooperationWarehouseId });
            const warehouseDetail = result.data?.getCooperationWarehouseById;
            if (warehouseDetail) {
                setSelectedCooperationWarehouse(warehouseDetail);
            } else {
                message.error('Cooperation warehouse detail not found');
                setCooperationWarehouseDrawerVisible(false);
            }
        } catch (error) {
            console.error('Failed to load cooperation warehouse details:', error);
            message.error('Failed to load cooperation warehouse details');
            setCooperationWarehouseDrawerVisible(false);
        }
    };

    const handleCooperationWarehouseDrawerSubmit = async (values: any) => {
        // 检查是否是从展示模式切换到编辑模式
        if (values._switchToEdit) {
            setCooperationWarehouseDrawerMode('edit');
            return; // 不关闭抽屉，继续编辑
        }

        // 编辑完成后关闭抽屉并刷新数据
        setCooperationWarehouseDrawerVisible(false);
        // 可以在这里添加刷新逻辑，如果需要的话
    };

    // 编辑状态
    const [editingStops, setEditingStops] = useState<{ [key: string]: any }>({});

    // 刷新任务详情数据的函数
    const refreshTaskDetail = async () => {
        try {
            await Promise.all([
                refetchDetail(),
                refetchAppointments()
            ]);
        } catch (error) {
            console.error('Failed to refresh task detail:', error);
        }
    };

    const handleUpdateUnloadRecordPhotos = async (unloadingId: number, photoS3Keys: string[]) => {
        try {
            const result = await updateUnloadRecordPhotos({
                variables: {
                    input: {
                        unloadingId,
                        photoS3Keys
                    }
                }
            });

            if (!result.data?.updateUnloadRecordPhotos) {
                message.error('Failed to update unload record photos');
                return;
            }

            message.success('Unload record photos updated');
            await refreshTaskDetail();
        } catch (error) {
            console.error('Failed to update unload record photos:', error);
            message.error('Failed to update unload record photos');
        }
    };

    // 根据任务状态确定字段是否可编辑
    const getFieldEditability = (fieldName: string): boolean => {
        if (!isEdit) return true; // 新建时所有字段都可编辑

        const status = detailData?.getPickupTaskDetail?.status;
        if (!status) return true;

        // 使用统一的字段映射表
        const allowedFields = EDITABLE_FIELDS_BY_STATUS[status as PickupTaskStatus] || [];
        return allowedFields.includes(fieldName);
    };

    // 检查任务是否处于只读状态（已完成或已取消）
    const isTaskReadOnly = (): boolean => {
        const status = detailData?.getPickupTaskDetail?.status;
        return status === PickupTaskStatus.COMPLETED || status === PickupTaskStatus.CANCELLED;
    };

    // 揽收任务操作权限检查
    const canOperatePickupTask = hasResource([ResourceCode.menu.PICKUP_TASK_OPERATE]);

    // 计算第一个有效预约单信息，用于动态更新起点选择
    const appointmentStops = appointmentsData?.getPickupTaskDetailItemList || [];
    const firstValidAppointment = useMemo(() => {
        if (!appointmentStops || appointmentStops.length === 0) return null;

        const validAppointments = appointmentStops
            .filter((item: PickupTaskDetailItem) =>
                !item.isUnloading &&
                item.status !== 'CANCELLED' &&
                item.status !== 'TRANSFER_OUT'
            )
            .sort((a: PickupTaskDetailItem, b: PickupTaskDetailItem) => (a.sequence || 0) - (b.sequence || 0));

        return validAppointments[0] || null;
    }, [appointmentStops]);

    // 新增：获取用于显示的第一个预约单地址（包括根据起点值匹配的情况）
    const getFirstAppointmentForDisplay = useMemo(() => {
        if (!appointmentStops || appointmentStops.length === 0) return null;

        // 首先尝试获取有效的预约单
        const validAppointments = appointmentStops
            .filter((item: PickupTaskDetailItem) =>
                !item.isUnloading &&
                item.status !== 'CANCELLED' &&
                item.status !== 'TRANSFER_OUT'
            )
            .sort((a: PickupTaskDetailItem, b: PickupTaskDetailItem) => (a.sequence || 0) - (b.sequence || 0));

        if (validAppointments.length > 0) {
            return validAppointments[0];
        }

        // 如果没有有效预约单，尝试根据当前起点的值去匹配
        if (detailData?.getPickupTaskDetail) {
            const taskDetail = detailData.getPickupTaskDetail;
            const startPointValue = taskDetail.startPointValue;

            if (startPointValue && taskDetail.startPointType === PickupTaskStartPointType.FIRST_APPOINTMENT) {
                // 尝试匹配pickupAppointmentId
                const matchedAppointment = appointmentStops.find((item: PickupTaskDetailItem) => {
                    if (item.isUnloading) return false;

                    const itemAppointmentId = item.pickupAppointmentId?.toString() || item.id?.toString();

                    return itemAppointmentId == startPointValue;
                });

                if (matchedAppointment) {
                    return matchedAppointment;
                }
            }
        }

        // 如果值匹配也未匹配到，获取第一条被CANCELLED/TRANSFER_OUT的数据
        const removedAppointments = appointmentStops
            .filter((item: PickupTaskDetailItem) =>
                !item.isUnloading &&
                (item.status === 'CANCELLED' || item.status === 'TRANSFER_OUT')
            )
            .sort((a: PickupTaskDetailItem, b: PickupTaskDetailItem) => (a.sequence || 0) - (b.sequence || 0));

        return removedAppointments[0] || null;
    }, [appointmentStops, detailData]);

    // 监听task变化，切换编辑/新增时重置表单
    useEffect(() => {
        if (isEdit && detailData?.getPickupTaskDetail) {
            const taskDetail = detailData.getPickupTaskDetail;

            // 准备起点和终点的值对象
            const startPoint: PointValue = {
                type: taskDetail.startPointType || PickupTaskStartPointType.HUB,
                value: taskDetail.startPointValue || ''
            };

            const endPoint: PointValue = {
                type: taskDetail.endPointType || PickupTaskEndPointType.HUB,
                value: taskDetail.endPointValue || ''
            };

            form.setFieldsValue({
                ...taskDetail,
                status: taskDetail.status || 'DRAFT',
                taskDate: taskDetail.taskDate ? dayjs(taskDetail.taskDate) : undefined,
                // 设置路线ID
                routeId: taskDetail.routeId || undefined,
                // 使用后端返回的本地时间字符串（At字段）来显示时间
                expDepartureTime: taskDetail.expDepartureTimeAt ? dayjs(taskDetail.expDepartureTimeAt, 'YYYY-MM-DD HH:mm:ss') : undefined,
                actDepartureTime: taskDetail.actDepartureTimeAt ? dayjs(taskDetail.actDepartureTimeAt, 'YYYY-MM-DD HH:mm:ss') : undefined,
                expCompleteTime: taskDetail.expCompleteTimeAt ? dayjs(taskDetail.expCompleteTimeAt, 'YYYY-MM-DD HH:mm:ss') : undefined,
                actCompleteTime: taskDetail.actCompleteTimeAt ? dayjs(taskDetail.actCompleteTimeAt, 'YYYY-MM-DD HH:mm:ss') : undefined,
                // 确保vehicleTypeId是字符串格式以匹配下拉框选项
                vehicleTypeId: taskDetail.vehicleTypeId?.toString() || undefined,
                // 设置起点和终点对象
                startPoint: {
                    type: startPoint.type,
                    value: startPoint.value,
                    name: taskDetail.startPointName || ''
                },
                endPoint: {
                    type: endPoint.type,
                    value: endPoint.value,
                    name: taskDetail.endPointName || ''
                }
            });

            // 设置车型ID状态以支持车辆联动
            const vtId = taskDetail.vehicleTypeId;
            if (vtId) {
                setSelectedVehicleModelId(vtId.toString());
            }
        } else {
            form.resetFields();
            setSelectedVehicleModelId(null);

            // 新建时设置默认值
            form.setFieldsValue({
                status: 'DRAFT'
            });
        }
    }, [taskId, visible, detailData]);

    // 处理第一个预约单地址的动态更新和自动切换
    useEffect(() => {
        if (!isEdit || !form || !detailData?.getPickupTaskDetail) return;

        const currentStartPoint = form.getFieldValue('startPoint');

        // 只有当起点类型是 FIRST_APPOINTMENT 时才处理
        if (currentStartPoint?.type === PickupTaskStartPointType.FIRST_APPOINTMENT) {
            if (firstValidAppointment) {
                // 有有效的第一个预约单，更新值
                const newAppointmentValue = firstValidAppointment.pickupAppointmentId?.toString() || firstValidAppointment.id?.toString() || '';
                const newAppointmentName = firstValidAppointment.address || 'First Appointment Address';

                // 只有当值真的发生变化时才更新，避免不必要的重新渲染
                if (currentStartPoint.value !== newAppointmentValue || currentStartPoint.name !== newAppointmentName) {
                    form.setFieldsValue({
                        startPoint: {
                            ...currentStartPoint,
                            value: newAppointmentValue,
                            name: newAppointmentName
                        }
                    });
                }
            }
        }
    }, [firstValidAppointment, isEdit, form, detailData, pointOptionsData]);


    const [createPickupTask] = useMutation(CREATE_PICKUP_TASK);
    const [updatePickupTask] = useMutation(UPDATE_PICKUP_TASK);
    const [updatePickupTaskStatus] = useMutation(UPDATE_PICKUP_TASK_STATUS);
    const [swapAppointments] = useMutation(SWAP_PICKUP_TASK_APPOINTMENTS, {
        refetchQueries: ['GetPickupTaskDetailItemList']
    });
    const [transferAppointment] = useMutation(TRANSFER_PICKUP_TASK_APPOINTMENT, {
        refetchQueries: [
            {
                query: GET_PICKUP_TASK_DETAIL_ITEMS,
                variables: {taskId}
            }
        ]
    });

    // 安全的保存方法 - 以原始数据为基础，避免字段丢失
    const handleSave = async (): Promise<void> => {
        try {
            // 先触发所有字段的校验
            await form.validateFields();

            // 获取表单值
            const values = form.getFieldsValue();

            // 提取起点和终点的值
            const startPoint = values.startPoint || {};
            const endPoint = values.endPoint || {};

            // 确保起点和终点的类型和值都存在
            let startPointType = startPoint.type || PickupTaskStartPointType.HUB;
            let startPointValue = startPoint.value || '';
            const endPointType = endPoint.type || PickupTaskEndPointType.HUB;
            const endPointValue = endPoint.value || '';

            // 特殊处理 FIRST_APPOINTMENT 类型的起点值
            if (startPointType === PickupTaskStartPointType.FIRST_APPOINTMENT) {
                // 确保值是有效的预约单ID
                if (!startPointValue && firstValidAppointment) {
                    startPointValue = firstValidAppointment.pickupAppointmentId?.toString() || '';
                }
                // 验证值是否有效，如果无效则提示用户
                if (!startPointValue || !firstValidAppointment) {
                    message.warning(t('pickup.pickupTask.pageDetails.messages.firstAppointmentAddressNotAvailable'));
                    setLoading(false);
                    return;
                }
            }
            setLoading(true);

            if (taskId) {
                // 编辑模式
                const originalData = detailData?.getPickupTaskDetail;
                const currentStatus = originalData?.status;

                // 已完成和已取消状态：不允许修改任何字段
                if (currentStatus === PickupTaskStatus.COMPLETED || currentStatus === PickupTaskStatus.CANCELLED) {
                    message.warning(t('pickup.pickupTask.pageDetails.messages.cannotUpdateTaskInCurrentStatus'));
                    setLoading(false);
                    return;
                }

                // 保留需要的字段 过滤掉非必要字段
                const cleanDataForBackend = (data: any) => {
                    if (!data) return {};

                    // 定义后端期望的字段（基于PickupTaskInput）
                    const allowedFields = [
                        'id', 'taskNumber', 'taskDate', 'warehouseId', 'accountId',
                        'vehicleId', 'vehicleTypeId', 'startPointType', 'startPointValue',
                        'endPointType', 'endPointValue', 'expDepartureTime', 'actDepartureTime',
                        'expCompleteTime', 'actCompleteTime', 'notes', 'status',
                        'estimatedDistanceKm', 'estimatedDurationMinutes',
                        'actualDistanceKm', 'actualDurationMinutes',
                        'routeOptimizationJson', 'createdAt', 'updatedAt'
                    ];

                    // 只保留允许的字段
                    const cleanData: any = {};
                    allowedFields.forEach(field => {
                        if (data[field] !== undefined) {
                            cleanData[field] = data[field];
                        }
                    });

                    return cleanData;
                };

                // 以原始数据为基础，确保不丢失任何字段
                const cleanOriginalData = cleanDataForBackend(originalData);
                

                
                const updateData = {
                    ...cleanOriginalData, // 保留所有原始字段
                    // 只更新表单中的字段（包含PickupTaskInput支持的字段）
                    taskDate: values.taskDate?.format('YYYY-MM-DD'),
                    accountId: values.accountId,
                    vehicleId: values.vehicleId,
                    vehicleTypeId: values.vehicleTypeId ? parseInt(values.vehicleTypeId) || originalData?.vehicleTypeId : originalData?.vehicleTypeId,
                    startPointType: startPointType,
                    startPointValue: startPointValue,
                    endPointType: endPointType,
                    endPointValue: endPointValue,
                    // 使用 At 字段格式（本地时间字符串），格式为 "YYYY-MM-DD HH:mm:ss"
                    expDepartureTimeAt: values.expDepartureTime ? values.expDepartureTime.format('YYYY-MM-DD HH:mm:ss') : originalData?.expDepartureTimeAt,
                    expCompleteTimeAt: values.expCompleteTime ? values.expCompleteTime.format('YYYY-MM-DD HH:mm:ss') : originalData?.expCompleteTimeAt,
                    actDepartureTimeAt: values.actDepartureTime ? values.actDepartureTime.format('YYYY-MM-DD HH:mm:ss') : originalData?.actDepartureTimeAt,
                    actCompleteTimeAt: values.actCompleteTime ? values.actCompleteTime.format('YYYY-MM-DD HH:mm:ss') : originalData?.actCompleteTimeAt,
                    notes: values.notes !== undefined ? values.notes : originalData?.notes,
                    estimatedDistanceKm: values.estimatedDistanceKm !== undefined ? values.estimatedDistanceKm : originalData?.estimatedDistanceKm,
                    estimatedDurationMinutes: values.estimatedDurationMinutes !== undefined ? values.estimatedDurationMinutes : originalData?.estimatedDurationMinutes,
                    // 确保关键系统字段不被覆盖
                    status: currentStatus,
                    id: originalData?.id,
                    createdAt: originalData?.createdAt,
                    updatedAt: originalData?.updatedAt
                };

                // 根据状态限制字段（如果业务需要）
                const allowedFields = EDITABLE_FIELDS_BY_STATUS[currentStatus as PickupTaskStatus] || [];
                if (allowedFields.length > 0) {
                    const restrictedData = {...cleanOriginalData};

                    // 明确的字段更新逻辑
                    if (allowedFields.includes('taskDate')) restrictedData.taskDate = updateData.taskDate;
                    if (allowedFields.includes('accountId')) restrictedData.accountId = updateData.accountId;
                    if (allowedFields.includes('vehicleId')) restrictedData.vehicleId = updateData.vehicleId;
                    if (allowedFields.includes('vehicleTypeId')) {
                        restrictedData.vehicleTypeId = updateData.vehicleTypeId;
                    }
                    if (allowedFields.includes('startPoint')) {
                        restrictedData.startPointType = updateData.startPointType;
                        restrictedData.startPointValue = updateData.startPointValue;
                    }
                    if (allowedFields.includes('endPoint')) {
                        restrictedData.endPointType = updateData.endPointType;
                        restrictedData.endPointValue = updateData.endPointValue;
                    }
                    if (allowedFields.includes('expDepartureTime')) {
                        restrictedData.expDepartureTimeAt = updateData.expDepartureTimeAt;
                    }
                    if (allowedFields.includes('expCompleteTime')) {
                        restrictedData.expCompleteTimeAt = updateData.expCompleteTimeAt;
                    }
                    if (allowedFields.includes('notes')) restrictedData.notes = updateData.notes;
                    if (allowedFields.includes('estimatedDistanceKm')) restrictedData.estimatedDistanceKm = updateData.estimatedDistanceKm;
                    if (allowedFields.includes('estimatedDurationMinutes')) restrictedData.estimatedDurationMinutes = updateData.estimatedDurationMinutes;


                    await updatePickupTask({variables: {taskId, input: restrictedData}});
                } else {


                    await updatePickupTask({variables: {taskId, input: updateData}});
                }

                message.success(t('pickup.pickupTask.pageDetails.messages.taskUpdatedSuccessfully'));
            } else {
                // 新建模式：直接使用表单数据
                const createData = {
                    taskDate: values.taskDate?.format('YYYY-MM-DD'),
                    warehouseId: warehouseId || "1",
                    accountId: values.accountId,
                    vehicleId: values.vehicleId,
                    vehicleTypeId: parseInt(values.vehicleTypeId) || undefined,
                    startPointType: startPointType,
                    startPointValue: startPointValue,
                    endPointType: endPointType,
                    endPointValue: endPointValue,
                    expDepartureTime: values.expDepartureTime ? values.expDepartureTime.utc().unix() : undefined,
                    notes: values.notes,
                    estimatedDistanceKm: values.estimatedDistanceKm,
                    estimatedDurationMinutes: values.estimatedDurationMinutes,
                    status: 'DRAFT'
                };


                await createPickupTask({variables: {input: createData}});
                message.success(t('pickup.pickupTask.pageDetails.messages.taskCreatedSuccessfully'));
            }

            setLoading(false);
            if (onSave) onSave({});
            onClose();
        } catch (e: any) {
            setLoading(false);
            console.error('Save failed:', e);
            message.error(e.message || 'Save failed');
        }
    };

    // 先获取数据，用于 useMemo 依赖
    const taskDetail = detailData?.getPickupTaskDetail;


    // 调试用：监控表单值变化
    useEffect(() => {
        const interval = setInterval(() => {
            const startPoint = form.getFieldValue('startPoint');
            const endPoint = form.getFieldValue('endPoint');
            const allValues = form.getFieldsValue();
        }, 2000);

        return () => clearInterval(interval);
    }, [form, isEdit, taskId]);
    // 提前return必须在所有hooks之后
    if (isEdit && (detailLoading || appointmentsLoading)) return <div
        style={{padding: 32}}>Loading...</div>;
    if (isEdit && !detailData?.getPickupTaskDetail) return <div style={{padding: 32}}>No detail found.</div>;

    // 调试信息
    if (appointmentsError) {
        console.error('Appointments query error:', appointmentsError);
    }
    // 获取状态标签颜色
    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'SUCCESS':
                return 'green';
            case 'COMPLETED':
                return 'blue';
            case 'IN_PROGRESS':
                return 'processing';
            case 'PENDING':
                return 'default';
            default:
                return 'default';
        }
    };

    // 获取状态显示文本
    const getStatusText = (status: string): string => {
        switch (status) {
            case 'SUCCESS':
                return 'Success';
            case 'COMPLETED':
                return 'Scheduled';
            case 'IN_PROGRESS':
                return 'In Progress';
            case 'PENDING':
                return 'Pending';
            default:
                return status;
        }
    };

    // 判断节点是否可移动（这是UI交互需要的）
    const isNodeMovable = (node: any): boolean => {
        // 已完成的节点不可移动
        if (node.status === PickupTaskNodeStatus.COMPLETED) {
            return false;
        }

        // 卸货记录不可移动
        if (node.isUnloading) {
            return false;
        }

        // 终点不可移动
        if (node.status === PickupTaskNodeStatus.END_POINT) {
            return false;
        }

        return true;
    };

    // 预约详情表格数据（直接使用后端返回的已排序数据）
    const appointmentTableData = appointmentStops;

    // 预约详情表格列定义
    const appointmentColumns: TableColumnsType<any> = [
        {
            title: '',
            dataIndex: 'sequence',
            key: 'sequence',
            width: 40,
            align: 'left',
            render: (value: number, record: any) => {
                // 终点不显示序号
                if (record.status === PickupTaskNodeStatus.END_POINT) {
                    return null;
                }
                return value;
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.stop'),
            dataIndex: 'site',
            key: 'site',
            width: 200,
            align: 'left',
            ellipsis: true,
            render: (text: string, record: any) => {
                if (record.isUnloading) {
                    // 卸货和终点显示hub名称
                    const displayText = record.hubName || text;
                    return (
                        <div className="text-gray-500">
                            {displayText}
                        </div>
                    );
                }

                // 检查是否为禁用状态（已取消或转移）
                const isDisabled = record.isCancelled || record.isTransferred || record.status === 'CANCELLED' || record.status === 'TRANSFER_OUT';

                // 预约单号显示为紫色，参考PickupTaskCreateDrawer的实现
                const displayText = record.appointmentNumber || record.status;
                let className = "";

                if (isDisabled) {
                    className = "text-gray-500";
                } else if (record.status === PickupTaskNodeStatus.COMPLETED) {
                    className = "text-gray-500";
                } else {
                    className = "text-purple-600 font-medium cursor-pointer hover:text-purple-800";
                }

                const handleAppointmentClick = () => {
                    if (!isDisabled && record.status !== PickupTaskNodeStatus.COMPLETED && record.pickupAppointmentId) {
                        setSelectedAppointmentId(Number(record.pickupAppointmentId));
                        setAppointmentDrawerVisible(true);
                    }
                };

                return (
                    <div className={className} onClick={handleAppointmentClick}>
                        {displayText}
                    </div>
                );
            }
        },
        {
            title: t('pickup.pickupTask.filters.pickupSite'),
            dataIndex: 'cooperationWarehouseId',
            key: 'cooperationWarehouseId',
            width: 140,
            align: 'left',
            render: (value: number | null | undefined, record: any) => {
                if (record.isUnloading) {
                    return '-';
                }
                if (!value) {
                    return '-';
                }
                return (
                    <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => showPickupSiteDetail(value)}
                    >
                        {value}
                    </Button>
                );
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.zipcode'),
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 80,
            align: 'left',
            render: (text: string, record: any) => {
                // 卸货和终点显示hub的邮编
                if (record.isUnloading) {
                    return <span className="text-gray-500">{record.hubZipcode || text}</span>;
                }
                // 预约单显示实际邮编
                return text;
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.address'),
            dataIndex: 'address',
            key: 'address',
            width: 300,
            align: 'left'
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            width: 60,
            align: 'left',
            render: (value: number, record: any) => {
                if (record.isUnloading) {
                    return '-';
                }
                return value;
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.unit'),
            dataIndex: 'unit',
            key: 'unit',
            width: 60,
            align: 'left',
            render: (value: string, record: any) => {
                if (record.isUnloading) {
                    return '-';
                }
                return formatUnit(value);
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.appointmentTime'),
            dataIndex: 'expectedPickupTime',
            key: 'appointmentTime',
            width: 120,
            align: 'left',
            render: (value: string, record: any) => {
                if (record.isUnloading) {
                    return '-';
                }

                // 使用期望揽收时间和期望到达时间拼接显示时间范围
                const expectedPickupTime = record.expectedPickupTime;
                const expectedArrivalTime = record.expectedArrivalTime;

                const formatTime = (timeValue: string | number): string | null => {
                    if (!timeValue) return null;
                    if (typeof timeValue === 'number') {
                        return dayjs(timeValue * 1000).format('HH:mm');
                    }
                    if (typeof timeValue === 'string' && timeValue.includes('T')) {
                        return dayjs(timeValue).format('HH:mm');
                    }
                    return timeValue;
                };

                const pickupTimeFormatted = formatTime(expectedPickupTime);
                const arrivalTimeFormatted = formatTime(expectedArrivalTime);

                // 如果两个时间都有，显示时间范围
                if (pickupTimeFormatted && arrivalTimeFormatted) {
                    return `${pickupTimeFormatted}-${arrivalTimeFormatted}`;
                }

                // 如果只有一个时间，显示单个时间
                if (pickupTimeFormatted) return pickupTimeFormatted;
                if (arrivalTimeFormatted) return arrivalTimeFormatted;

                return '-';
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.actualQuantity'),
            dataIndex: 'actualQuantity',
            key: 'actualQuantity',
            width: 60,
            align: 'left',
            render: (value: number, record: any) => {
                if (record.isUnloading) {
                    return record.status === PickupTaskNodeStatus.END_POINT ? '-' : '-';
                }
                return value || '-';
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.actualUnit'),
            dataIndex: 'actualUnit',
            key: 'actualUnit',
            width: 60,
            align: 'left',
            render: (value: string, record: any) => {
                if (record.isUnloading) {
                    return '-';
                }
                return formatUnit(value);
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.actualTime'),
            dataIndex: 'actualTime',
            key: 'actualTime',
            width: 80,
            align: 'left',
            render: (value: string, record: any) => {
                if (record.isUnloading) {
                    return record.createdAt ? dayjs(record.createdAt).format('HH:mm') : '-';
                }
                if (value) {
                    if (record.actualPickupTime != null && record.actualPickupTime != '' && record.actualPickupTime.includes('T')){
                        return dayjs(record.actualPickupTime).format('HH:mm:ss')
                    }
                    // If it's a timestamp, format it
                    if (typeof value === 'number') {
                        return dayjs(value * 1000).format('HH:mm');
                    }
                    // If it's a date string, format it
                    if (typeof value === 'string' && value.includes('T')) {
                        return dayjs(value).format('HH:mm');
                    }
                    // If it's already a formatted string, return as is
                    return value;
                }
                return '-';
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.distance'),
            dataIndex: 'distance',
            key: 'distance',
            width: 80,
            align: 'left',
            render: (value: string) => value || '-'
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.duration'),
            dataIndex: 'duration',
            key: 'duration',
            width: 80,
            align: 'left',
            render: (value: string) => value || '-'
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            align: 'left',
            render: (status: string, record: any) => {
                if (record.isUnloading) {
                    return null;
                }
                // 将 CANCELLED 状态显示为 REMOVED
                if (status === 'CANCELLED' || record.isCancelled) {
                    return 'REMOVED';
                }
                return status;
            }
        },
        {
            title: t('pickup.pickupTask.pageDetails.columns.action'),
            key: 'actions',
            align: 'left',
            width: 200,
            fixed: 'right',
            render: (_, record: any, index: number) => {
                // 如果是卸货记录或终点，不显示任何操作按钮
                if (record.isUnloading) return null;

                // 如果预约单已完成，不显示任何操作按钮
                if (record.status === PickupTaskNodeStatus.COMPLETED) return null;

                // 已转出：操作框留空，不支持任何操作（状态列已展示 TRANSFER_OUT，此处不再重复）
                if (record.isTransferred || record.status === 'TRANSFER_OUT') {
                    return null;
                }

                // 如果预约单已被取消，不显示任何操作按钮
                if (record.isCancelled || record.status === 'CANCELLED') {
                    return (
                        <div className="text-xs text-gray-400">
                            {t('pickup.pickupTask.pageDetails.columns.removed')}
                        </div>
                    );
                }

                // 如果预约单已揽收失败或已揽收，不显示任何操作按钮
                if (record.status === 'PICKUP_FAILED' || record.status === 'PICKED_UP') {
                    return (
                        <div className="text-xs text-gray-400">
                            {record.status === 'PICKUP_FAILED' ? 'Failed' : 'Picked Up'}
                        </div>
                    );
                }

                // 获取排序后的所有预约单（非卸货记录）
                const appointmentItems = appointmentTableData.filter((item: any) => !item.isUnloading);
                // 当前预约单在排序后数据中的索引
                const appointmentIndex = appointmentItems.findIndex((item: any) => item.id === record.id);


                // 简化的按钮逻辑：只检查位置和整行禁用状态
                const canMoveUp = (): boolean => {
                    // 如果当前记录已被取消或转移，不可移动
                    if (record.isCancelled || record.isTransferred || record.status === 'CANCELLED' || record.status === 'TRANSFER_OUT') return false;

                    // 如果当前记录已揽收失败或已揽收，不可移动
                    if (record.status === 'PICKUP_FAILED' || record.status === 'PICKED_UP') return false;

                    // 第一条预约单不可上移
                    if (appointmentIndex === 0) return false;

                    // 检查当前记录是否为整行禁用状态
                    if (record.isCancelled || record.isTransferred ||
                        record.status === 'CANCELLED' || record.status === 'TRANSFER_OUT' ||
                        record.status === 'PICKUP_FAILED' || record.status === 'PICKED_UP') {
                        return false;
                    }

                    return true;
                };

                const canMoveDown = (): boolean => {
                    // 最后一条预约单不可下移
                    if (appointmentIndex >= appointmentItems.length - 1) return false;

                    // 检查当前记录是否为整行禁用状态
                    if (record.isCancelled || record.isTransferred ||
                        record.status === 'CANCELLED' || record.status === 'TRANSFER_OUT' ||
                        record.status === 'PICKUP_FAILED' || record.status === 'PICKED_UP') {
                        return false;
                    }

                    return true;
                };

                return (
                    <Space size="small" className="w-full">
                        {/* 上下移按钮区域，固定宽度 */}
                        <div className="w-[60px] flex gap-1 justify-start">
                            <Button
                                type="link"
                                size="small"
                                className="p-0 min-w-[24px]"
                                icon={<ArrowUpOutlined/>}
                                onClick={() => handleMoveStop(Number(record.id), 'up')}
                                disabled={!canMoveUp() || !record.id || isTaskReadOnly() || tableLoading || !canOperatePickupTask}
                            />
                            <Button
                                type="link"
                                size="small"
                                className="p-0 min-w-[24px]"
                                icon={<ArrowDownOutlined/>}
                                onClick={() => handleMoveStop(Number(record.id), 'down')}
                                disabled={!canMoveDown() || !record.id || isTaskReadOnly() || tableLoading || !canOperatePickupTask}
                            />
                        </div>
                        {/* 分隔线 */}
                        <Divider type="vertical" className="h-[20px] m-0"/>
                        {/* 其他操作按钮 */}
                        <div className="flex gap-4">
                            <Button
                                type="link"
                                size="small"
                                className="p-0 text-xs min-w-[48px]"
                                onClick={() => handleRemoveStop(record.id)}
                                disabled={record.isCancelled || record.isTransferred || record.status === 'CANCELLED' || record.status === 'TRANSFER_OUT' || record.status === 'PICKUP_FAILED' || record.status === 'PICKED_UP' || isTaskReadOnly() || tableLoading || !canOperatePickupTask}
                            >
                                {t('pickup.pickupTask.pageDetails.columns.removed')}
                            </Button>
                            <Button
                                type="link"
                                size="small"
                                className="p-0 text-xs min-w-[48px]"
                                onClick={() => handleTransferStop(record.id)}
                                disabled={record.isCancelled || record.isTransferred || record.status === 'CANCELLED' || record.status === 'TRANSFER_OUT' || record.status === 'PICKUP_FAILED' || record.status === 'PICKED_UP' || isTaskReadOnly() || tableLoading || !canOperatePickupTask}
                            >
                                {t('pickup.pickupTask.pageDetails.columns.transfer')}
                            </Button>
                        </div>
                    </Space>
                );
            }
        }
    ];

    // 照片上传配置
    const uploadProps: UploadProps = {
        name: 'file',
        action: '/api/upload',
        headers: {
            authorization: 'authorization-text',
        },
        onChange(info): void {
            if (info.file.status === 'done') {
                message.success(`${info.file.name} uploaded successfully`);
            } else if (info.file.status === 'error') {
                message.error(`${info.file.name} upload failed`);
            }
        },
    };

    // 前端计算统计数据
    const calculateStatistics = () => {
        // 过滤有效的预约单：排除卸货记录和无效状态的预约单
        const validAppointmentItems = appointmentStops.filter((item: PickupTaskDetailItem) => {
            // 排除卸货记录
            if (item.isUnloading) return false;

            // 只排除真正取消的状态：CANCELLED 和 TRANSFER_OUT
            // PICKUP_FAILED 是正常的揽收操作，需要统计
            const invalidStatuses = ['CANCELLED', 'TRANSFER_OUT'];
            if (invalidStatuses.includes(item.status)) return false;

            return true;
        });

        const appointmentCount = validAppointmentItems.length;

        // 按单位汇总货量
        const quantityByUnit: { [key: string]: number } = {};
        validAppointmentItems.forEach((item: PickupTaskDetailItem) => {
            const unit = item.unit || 'PCS'; // 默认单位为 PCS
            const quantity = item.quantity || 0;
            quantityByUnit[unit] = (quantityByUnit[unit] || 0) + quantity;
        });

        // 格式化货量显示
        const formatLoad = () => {
            const units = Object.keys(quantityByUnit);
            if (units.length === 0) return '-';

            const loadParts = units.map(unit => {
                const unitLabel = packageUnitFilterOptions.find(option => option.value === unit)?.label || unit;
                return `${quantityByUnit[unit]}${unitLabel}`;
            });

            return loadParts.join('+');
        };

        return {
            appointmentCount,
            totalLoad: formatLoad(),
            estimatedDistance: '-',
            estimatedDuration: '-',
            loadRate: '-'
        };
    };

    // 处理转到操作
    const handleTransferStop = (stopId: string): void => {
        setSelectedStopId(stopId);
        setTransferModalVisible(true);
    };

    // 处理取消站点
    const handleRemoveStop = async (taskAppointmentId: string): Promise<void> => {

        const now = Date.now();
        if (tableLoading || (now - lastClickTime < CLICK_THROTTLE_MS)) {
            if (now - lastClickTime < CLICK_THROTTLE_MS) {
                message.warning(t('pickup.pickupTask.pageDetails.messages.pleaseWaitBeforeNextOperation'));
            }
            return;
        }
        try {
            setLastClickTime(now);
            setTableLoading(true);
            if (!taskId) {
                message.error(t('pickup.pickupTask.pageDetails.messages.taskIdRequired'));
                return;
            }

            // 找到要取消的预约单，获取其pickupAppointmentId
            const appointmentToRemove = appointmentStops.find((item: PickupTaskDetailItem) => item.id?.toString() === taskAppointmentId);
            if (!appointmentToRemove || appointmentToRemove.isUnloading) {
                message.error(t('pickup.pickupTask.pageDetails.messages.appointmentNotFound'));
                return;
            }

            // 调用转移接口，不传targetTaskId表示取消
            await transferAppointment({
                variables: {
                    sourceTaskId: taskId.toString(),
                    targetTaskId: null, // null表示仅取消，不转移到其他任务
                    appointmentIds: [appointmentToRemove.pickupAppointmentId.toString()]
                }
            });

            message.success(t('pickup.pickupTask.pageDetails.messages.stopRemovedFromTaskSuccessfully'));
        } catch (error) {
            console.error('Failed to cancel stop:', error);
            message.error(t('pickup.pickupTask.pageDetails.messages.failedToRemoveStop'));
        } finally {
            // 清除loading状态
            setTableLoading(false);
        }
    };

    // 处理添加站点
    const handleAddStop = (): void => {
        // 防抖检查
        const now = Date.now();
        if (tableLoading || (now - lastClickTime < CLICK_THROTTLE_MS)) {
            if (now - lastClickTime < CLICK_THROTTLE_MS) {
                message.warning(t('pickup.pickupTask.pageDetails.messages.pleaseWaitBeforeNextOperation'));
            }
            return;
        }
        // 设置防抖时间
        setLastClickTime(now);
        setAddModalVisible(true);
    };

    // 处理预约单的转移或绑定
    const handleTransferOrBindAppointments = async (
        targetTaskId: string,
        appointmentIds: string[],
        sourceTaskId?: string,
        isTransfer: boolean = true
    ): Promise<void> => {
        if (!appointmentIds.length) return;

        try {
            await transferAppointment({
                variables: {
                    sourceTaskId: sourceTaskId || null,  // 如果是绑定场景，使用null
                    targetTaskId: targetTaskId,
                    appointmentIds: appointmentIds
                }
            });
            message.success(isTransfer ? t('pickup.pickupTask.pageDetails.messages.appointmentTransferredSuccessfully') : t('pickup.pickupTask.pageDetails.messages.appointmentsBoundSuccessfully'));
        } catch (error: any) {
            console.error('Failed to process appointments:', error);

            // 提取详细的错误信息
            let errorMessage = isTransfer ? t('pickup.pickupTask.pageDetails.messages.failedToTransferAppointment') : t('pickup.pickupTask.pageDetails.messages.failedToBindAppointments');

            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                // 优先使用 GraphQL 错误信息
                const graphQLError = error.graphQLErrors[0];
                if (graphQLError.extensions?.message) {
                    errorMessage = graphQLError.extensions.message;
                } else if (graphQLError.message) {
                    errorMessage = graphQLError.message;
                }
            } else if (error.message) {
                // 使用通用错误信息
                errorMessage = error.message;
            }

            message.error(errorMessage);
        }
    };

    // 处理确认转到其他任务（原handleConfirmTransfer的场景）
    const handleConfirmTransfer = async (targetTaskId: string): Promise<void> => {
        if (!taskId || !selectedStopId) return;

        // 找到选中的预约单，获取其pickupAppointmentId
        const selectedAppointment = appointmentStops.find((item: PickupTaskDetailItem) => item.id?.toString() === selectedStopId);
        if (!selectedAppointment || selectedAppointment.isUnloading) {
            message.error(t('pickup.pickupTask.pageDetails.messages.selectedAppointmentNotFound'));
            return;
        }

        await handleTransferOrBindAppointments(
            targetTaskId,
            [selectedAppointment.pickupAppointmentId.toString()],
            taskId.toString(),
            true
        );
    };

    // 处理直接绑定预约单到任务（原handleBindAppointments的场景）
    const handleConfirmAddAppointments = async (appointmentIds: string[]): Promise<void> => {
        if (!taskId) return;
        // 这里的appointmentIds已经是pickupAppointmentId，可以直接使用
        await handleTransferOrBindAppointments(taskId.toString(), appointmentIds, undefined, false);
    };


    // 处理任务操作
    const handleCancelTask = async (): Promise<void> => {
        if (!taskId) return;

        const now = Date.now();
        if (loading || (now - lastClickTime < CLICK_THROTTLE_MS)) {
            if (now - lastClickTime < CLICK_THROTTLE_MS) {
                message.warning(t('pickup.pickupTask.pageDetails.messages.pleaseWaitBeforeNextOperation'));
            }
            return;
        }

        setLastClickTime(now);
        setLoading(true);
        try {
            await updatePickupTaskStatus({
                variables: {taskId, status: PickupTaskStatus.CANCELLED, warehouseId},
            });
            message.success(t('pickup.pickupTask.pageDetails.messages.taskCancelledSuccessfully'));
            onClose();
            if (onSave) onSave({});
        } catch (e: any) {
            message.error(e.message || 'Cancel failed');
        }
        setLoading(false);
    };

    const handlePublishTask = async (): Promise<void> => {
        if (!taskId) return;
        
        // 首先验证表单必填字段
        try {
            await form.validateFields(['expDepartureTime', 'expCompleteTime']);
        } catch (error) {
            // 表单验证失败
            return;
        }
        
        const now = Date.now();
        if (loading || (now - lastClickTime < CLICK_THROTTLE_MS)) {
            if (now - lastClickTime < CLICK_THROTTLE_MS) {
                message.warning(t('pickup.pickupTask.pageDetails.messages.pleaseWaitBeforeNextOperation'));
            }
            return;
        }
        setLastClickTime(now);
        setLoading(true);
        try {
            await updatePickupTaskStatus({
                variables: {taskId, status: PickupTaskStatus.PUBLISHED, warehouseId},
            });
            message.success(t('pickup.pickupTask.pageDetails.messages.taskPublishedSuccessfully'));
            onClose();
            if (onSave) onSave({});
        } catch (e: any) {
            console.error('Failed to publish task:', e);

            // 提取详细的错误信息
            let errorMessage = 'Publish failed';
            if (e.graphQLErrors && e.graphQLErrors.length > 0) {
                // 优先使用 GraphQL 错误信息
                const graphQLError = e.graphQLErrors[0];
                if (graphQLError.extensions?.message) {
                    errorMessage = graphQLError.extensions.message;
                } else if (graphQLError.message) {
                    errorMessage = graphQLError.message;
                }
            } else if (e.message) {
                // 使用通用错误信息
                errorMessage = e.message;
            }
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTask = (): void => {
        setLoading(true);
        // 模拟API调用
        setTimeout((): void => {
            setLoading(false);
            message.success(t('pickup.pickupTask.pageDetails.messages.taskUpdatedSuccessfully'));
            onClose();
        }, 1000);
    };

    // 处理移动站点
    const handleMoveStop = async (currentTaskAppointmentId: number, direction: 'up' | 'down'): Promise<void> => {
        const now = Date.now();
        if (tableLoading || (now - lastClickTime < CLICK_THROTTLE_MS)) {
            if (now - lastClickTime < CLICK_THROTTLE_MS) {
                message.warning(t('pickup.pickupTask.pageDetails.messages.pleaseWaitBeforeNextOperation'));
            }
            return;
        }
        try {
            setLastClickTime(now);
            setTableLoading(true);
            if (!taskId) {
                message.error(t('pickup.pickupTask.pageDetails.messages.taskIdRequired'));
                return;
            }
            // 找到当前项目在列表中的位置
            const currentIndex = appointmentStops.findIndex((item: PickupTaskDetailItem): boolean => {
                // 确保类型匹配，处理null值
                return item.id !== null && Number(item.id) === Number(currentTaskAppointmentId);
            });

            if (currentIndex === -1) {
                console.error('Current appointment not found:', {
                    currentTaskAppointmentId,
                    availableIds: appointmentStops.map((item: PickupTaskDetailItem) => item.id)
                });
                message.error(t('pickup.pickupTask.pageDetails.messages.currentAppointmentNotFound'));
                return;
            }

            // 根据方向找到目标项目，跳过卸货记录
            let targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
            let targetItem = appointmentStops[targetIndex];

            // 跳过卸货记录，找到下一个预约单
            while (targetItem && targetItem.isUnloading) {
                targetIndex = direction === 'up' ? targetIndex - 1 : targetIndex + 1;
                targetItem = appointmentStops[targetIndex];
            }

            if (!targetItem || targetItem.isUnloading || !targetItem.id) {
                message.warning(t('pickup.pickupTask.pageDetails.messages.noValidTargetAppointmentFound'));
                return;
            }
            // 调用后端API交换顺序 - 传递的是pickup_task_appointment表的ID
            await swapAppointments({
                variables: {
                    taskId,
                    firstAppointmentId: currentTaskAppointmentId.toString(),
                    secondAppointmentId: targetItem.id.toString()
                }
            });

            message.success(t('pickup.pickupTask.pageDetails.messages.stopMovedSuccessfully', {direction}));
            // 等待数据刷新完成后再清除 loading 状态
            // 确保数据完全刷新和前端排序完成
            await new Promise(resolve => setTimeout(resolve, 500));

        } catch (error: any) {
            console.error('Failed to move stop:', error);
            // 提取详细的错误信息
            let errorMessage = t('pickup.pickupTask.pageDetails.messages.failedToMoveStop');
            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                // 优先使用 GraphQL 错误信息
                const graphQLError = error.graphQLErrors[0];
                if (graphQLError.extensions?.message) {
                    errorMessage = graphQLError.extensions.message;
                } else if (graphQLError.message) {
                    errorMessage = graphQLError.message;
                }
            } else if (error.message) {
                // 使用通用错误信息
                errorMessage = error.message;
            }
            message.error(errorMessage);
        } finally {
            // 清除表格 loading 状态
            setTableLoading(false);
        }
    };

    return (
        <Drawer
            className="[&_.ant-form-item-label]:!text-left [&_.ant-form-item-label>label]:!justify-start [&_.ant-form-item-label>label]:!w-full"
            title={
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <span className="h-5 border-l-4 border-purple-500 mr-2 mt-1"></span>
                        <span
                            className="text-xl font-medium text-black">{isEdit ? t('pickup.pickupTask.pageDetails.titles.editPickupTask') : t('pickup.pickupTask.pageDetails.titles.createPickupTask')}</span>
                    </div>
                </div>
            }
            width={1100}
            placement="right"
            closable={false}
            onClose={onClose}
            open={visible}
            footer={
                <div className="flex justify-between items-center">
                    {isEdit ? (
                        <>
                            <div className="flex items-center gap-2">
                                {/* 只读状态不显示任何操作按钮 */}
                                {!isTaskReadOnly() && (
                                    <>
                                        {/* 只有草稿和已发布状态可以取消任务 */}
                                        {(detailData?.getPickupTaskDetail?.status === PickupTaskStatus.DRAFT ||
                                            detailData?.getPickupTaskDetail?.status === PickupTaskStatus.PUBLISHED) && (
                                            <Button
                                                onClick={handleCancelTask}
                                                disabled={!canOperatePickupTask}
                                                className="w-auto min-w-24"
                                            >
                                                <span className="font-bold">{t('pickup.pickupTask.pageDetails.buttons.cancelTask')}</span>
                                            </Button>
                                               
                                        )}
                                        {/* 只有草稿状态可以发布任务 */}
                                        {detailData?.getPickupTaskDetail?.status === PickupTaskStatus.DRAFT && (
                                            <Button
                                                onClick={handlePublishTask}
                                                disabled={!canOperatePickupTask}
                                                className="w-auto min-w-24"
                                            >
                                                <span className="font-bold">{t('pickup.pickupTask.pageDetails.buttons.publishTask')}</span>
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button onClick={onClose} className="w-auto min-w-24">
                                    <span className="font-bold">{t('pickup.pickupTask.pageDetails.buttons.close')}</span>
                                </Button>
                                {/* 只读状态不显示更新按钮 */}
                                {!isTaskReadOnly() && (
                                    <Button
                                        type="primary"
                                        onClick={handleSave}
                                        loading={loading}
                                        disabled={!canOperatePickupTask}
                                        className="w-auto min-w-24"
                                    >
                                        <span className="font-bold">{t('pickup.pickupTask.pageDetails.buttons.update')}</span>
                                    </Button>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="w-full flex justify-end gap-2">
                            <Button onClick={onClose} className="w-auto min-w-24">
                                <span className="font-bold">{t('components.button.cancel')}</span>
                            </Button>
                            <Button type="primary" onClick={handleSave} loading={loading} disabled={!canOperatePickupTask} className="w-auto min-w-24">
                                <span className="font-bold">{t('pickup.pickupTask.pageDetails.buttons.create')}</span>
                            </Button>
                        </div>
                    )}
                </div>
            }
        >
            <Form
                form={form}
                layout="horizontal"
                className="mb-6"
                labelCol={{span: 8}}
                wrapperCol={{span: 16}}
            >
                <div className="border-b border-gray-200 pb-4 mb-4">
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            {isEdit && (
                                <Form.Item
                                    label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.taskNumber')}</span>}
                                    name="taskNumber"
                                    className="mb-2"
                                >
                                    <Input placeholder="" disabled/>
                                </Form.Item>
                            )}
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.taskStatus')}</span>}
                                name="status"
                                className="mb-2"
                            >
                                <Input disabled/>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.taskDate')}</span>}
                                name="taskDate"
                                rules={[{required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectTaskDate') || ''}]}
                                className="mb-2"
                            >
                                <DatePicker
                                    format="YYYY/MM/DD"
                                    className="w-full"
                                    disabled={true}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <div className="flex w-full">
                                <div className="w-[94%]" >
                                    {(isEdit && !getFieldEditability('accountId')) ? (
                                          <Form.Item
                                          label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.driver')}</span>}
                                          rules={[{required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectDriver') || ''}]}
                                          className="mb-2"
                                      >
                                        <Input
                                            className="w-full"
                                            disabled
                                            style={{marginLeft: '10px',width: '95%'}}
                                            value={
                                                detailData?.getPickupTaskDetail?.driverName ||
                                                filteredDrivers.find(
                                                    (driver) => driver.accountId === form.getFieldValue('accountId')
                                                )?.driverName ||
                                                ''
                                            }
                                        />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item
                                            label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.driver')}</span>}
                                            name="accountId"
                                            rules={[{required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectDriver') || ''}]}
                                            className="mb-2"

                                        >
                                            <Select
                                                className="w-full"
                                                loading={driversLoading}
                                                disabled={isEdit && !getFieldEditability('accountId')}
                                                showSearch
                                                optionFilterProp="children"
                                                style={{marginLeft: '10px',width: '95%'}}
                                                placeholder={t('pickup.pickupTask.pageDetails.placeholders.selectDriver') || ''}
                                                onChange={handleDriverChange}
                                            >
                                                {filteredDrivers.map((driver) => (
                                                    <Select.Option key={driver.accountId} value={driver.accountId}>
                                                        {driver.driverName}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                </div>
                                <div className="w-[6%] flex items-center justify-start">
                                    <CopyOutlined
                                        style={{ marginBottom: '10px' }}
                                        className="cursor-pointer text-gray-400 hover:text-gray-600"
                                        onClick={handleCopyDriver}
                                    />
                                </div>
                            </div>
                        </Col>

                        {/* 隐藏的路线ID字段，用于传递数据到后端 */}
                        <Form.Item
                            name="routeId"
                            hidden={true}
                        >
                            <Input type="hidden" />
                        </Form.Item>

                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.origin')}</span>}
                                name="startPoint"
                                rules={[{
                                    required: true,
                                    message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectOrigin') || '',
                                    validator: (_, value): Promise<void> => {
                                        if (value && value.type !== undefined && value.value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('pickup.pickupTask.pageDetails.messages.pleaseSelectOrigin') || ''));
                                    }
                                }]}
                                className="mb-2"
                            >
                                <PointSelector
                                    type="start"
                                    warehouseId={warehouseId}
                                    taskId={taskId || undefined}
                                    placeholder={t('pickup.pickupTask.pageDetails.placeholders.selectOrigin') || ''}
                                    disabled={isEdit && !getFieldEditability('startPoint')}
                                    customPointOptions={
                                        pointOptionsData?.getPickupPointOptions ? {
                                            hubList: pointOptionsData.getPickupPointOptions.hubList || [],
                                            rallyPointList: pointOptionsData.getPickupPointOptions.rallyPointList || [],
                                            // 使用新的逻辑：优先显示有效预约单，否则显示根据起点值匹配的预约单
                                            firstAppointmentAddress: getFirstAppointmentForDisplay ? {
                                                id: getFirstAppointmentForDisplay.pickupAppointmentId?.toString() || getFirstAppointmentForDisplay.id?.toString() || '',
                                                name: getFirstAppointmentForDisplay.address || 'First Appointment Address',
                                                type: 'FIRST_APPOINTMENT' as const,
                                                value: getFirstAppointmentForDisplay.pickupAppointmentId?.toString() || getFirstAppointmentForDisplay.id?.toString() || '',
                                                fullAddress: getFirstAppointmentForDisplay.address || '',
                                                phone: ''
                                            } : undefined,
                                            warehouseList: pointOptionsData.getPickupPointOptions.warehouseList || [],
                                        } : undefined
                                    }
                                    onChange={(complexValue: string | null, option: any) => {
                                        if (!complexValue) {
                                            form.setFieldsValue({
                                                startPoint: undefined
                                            });
                                            return;
                                        }

                                        const [pointType, pointValue] = complexValue.split('-');
                                        const pointTypeInt = parseInt(pointType);

                                        let pointName = '';
                                        if (option?.children) {
                                            pointName = option.children;
                                        } else if (option?.['data-name']) {
                                            pointName = option['data-name'];
                                        }

                                        const startPointValue = {
                                            type: pointTypeInt,
                                            value: pointValue,
                                            name: pointName
                                        };

                                        form.setFieldsValue({
                                            startPoint: startPointValue
                                        });
                                        if (complexValue !== form.getFieldValue('startPoint')?.value) {
                                            setTimeout(() => {
                                                form.validateFields(['startPoint']);
                                            }, 0);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.vehicleModel')}</span>}
                                name="vehicleTypeId"
                                rules={[{required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectVehicleType') || ''}]}
                                className="mb-2"
                            >
                                <Select
                                    className="w-full"
                                    placeholder={t('pickup.pickupTask.pageDetails.placeholders.selectVehicleType') || ''}
                                    showSearch
                                    loading={vehicleModelsLoading}
                                    disabled={isEdit && !getFieldEditability('vehicleTypeId')}
                                    optionFilterProp="children"
                                    onChange={handleVehicleModelChange}
                                >
                                    {(vehicleModelsData || []).map((vt: any) => (
                                        <Select.Option key={vt.id} value={vt.id.toString()}>
                                            {vt.nameEn}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.destination')}</span>}
                                name="endPoint"
                                rules={[{
                                    required: true,
                                    message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectDestination') || '',
                                    validator: (_, value): Promise<void> => {
                                        if (value && value.value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('pickup.pickupTask.pageDetails.messages.pleaseSelectDestination') || ''));
                                    }
                                }]}
                                className="mb-2"
                            >
                                <PointSelector
                                    key={`end-${warehouseId}`}
                                    type="end"
                                    warehouseId={warehouseId}
                                    taskId={taskId || undefined}
                                    placeholder={t('pickup.pickupTask.pageDetails.placeholders.selectDestination') || ''}
                                    disabled={isEdit && !getFieldEditability('endPoint')}
                                    onChange={(complexValue, option: any): void => {
                                        if (!complexValue) return;

                                        const [pointType, pointValue] = complexValue.split('-');
                                        form.setFieldsValue({
                                            endPoint: {
                                                type: parseInt(pointType),
                                                value: pointValue,
                                                name: complexValue
                                            }
                                        });

                                        // 只在用户主动选择时触发验证，回显时不验证
                                        if (complexValue !== form.getFieldValue('endPoint')?.value) {
                                            setTimeout((): void => {
                                                form.validateFields(['endPoint']);
                                            }, 0);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<span className="text-gray-500">{t('pickup.pickupTask.pageDetails.fields.vehicle')}</span>}
                                name="vehicleId"
                                rules={[{required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectVehicle') || ''}]}
                                className="mb-2"
                            >
                                <Select
                                    className="w-full"
                                    loading={vehiclesLoading}
                                    disabled={isEdit && !getFieldEditability('vehicleId')}
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder={selectedVehicleModelId ? t('pickup.pickupTask.pageDetails.placeholders.selectVehicle') || '' : t('pickup.pickupTask.pageDetails.placeholders.pleaseSelectVehicleTypeFirst') || ''}
                                >
                                    {getFilteredVehicles().map((vehicle) => (
                                        <Select.Option key={vehicle.value} value={vehicle.value.toString()}>
                                            {vehicle.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={
                                    <span className="text-gray-500 break-words whitespace-normal">
                                        {t('pickup.pickupTask.pageDetails.fields.expectedDepartureTime')}
                                    </span>
                                }
                                name="expDepartureTime"
                                rules={[
                                    {required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectExpectedDepartureTime') || ''},
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();

                                            const expCompleteTime = form.getFieldValue('expCompleteTime');
                                            if (!expCompleteTime) return Promise.resolve();

                                            // 检查是否在expCompleteTime之前
                                            if (value.isAfter(expCompleteTime)) {
                                                return Promise.reject(new Error(t('pickup.pickupTask.pageDetails.messages.expectedDepartureTimeMustBeBeforeExpectedCompleteTime') || ''));
                                            }

                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                className="mb-2"
                            >
                                <DatePicker
                                    showTime
                                    format="YYYY/MM/DD HH:mm"
                                    className="w-full"
                                    disabled={isEdit && !getFieldEditability('expDepartureTime')}
                                    disabledDate={(current) => {
                                        const taskDate = form.getFieldValue('taskDate');
                                        if (!taskDate || !current) return false;

                                        // 直接比较日期，不使用时区转换
                                        // 只允许选择taskDate当天
                                        return !current.isSame(taskDate, 'day');
                                    }}
                                    disabledTime={(current) => {
                                        if (!current) return {};

                                        const taskDate = form.getFieldValue('taskDate');
                                        if (!taskDate) return {};

                                        // 直接比较日期，不使用时区转换
                                        // 如果不是当天，禁用所有时间
                                        if (!current.isSame(taskDate, 'day')) {
                                            return {
                                                disabledHours: () => Array.from({length: 24}, (_, i) => i),
                                                disabledMinutes: () => Array.from({length: 60}, (_, i) => i),
                                                disabledSeconds: () => Array.from({length: 60}, (_, i) => i)
                                            };
                                        }

                                        // 如果是当天，允许所有时间（Expected Departure Time不受pickup deadline限制）
                                        return {};
                                    }}
                                    onChange={() => {
                                        // 当Expected Departure Time改变时，重新验证Expected Complete Time
                                        const expCompleteTime = form.getFieldValue('expCompleteTime');
                                        if (expCompleteTime) {
                                            form.validateFields(['expCompleteTime']).catch(() => {
                                                // 验证失败时不需要特殊处理，错误信息会自动显示
                                            });
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            {isEdit ? (
                                <Form.Item
                                    label={<span
                                        className="text-gray-500 whitespace-nowrap">{t('pickup.pickupTask.pageDetails.fields.actualDepartureTime')}</span>}
                                    name="actDepartureTime"
                                    className="mb-2"
                                >
                                    <DatePicker
                                        showTime
                                        format="YYYY/MM/DD HH:mm"
                                        className="w-full"
                                        disabled
                                    />
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    label={<span
                                        className="text-gray-500 whitespace-nowrap">{t('pickup.pickupTask.pageDetails.fields.actualDepartureTime')}</span>}
                                    className="mb-2"
                                >
                                    <span className="text-gray-400">-</span>
                                </Form.Item>
                            )}
                        </Col>

                        {isEdit && (
                            <>
                                <Col span={12}>
                                    <Form.Item
                                        label={
                                            <span className="text-gray-500 break-words whitespace-normal">
                                                {t('pickup.pickupTask.pageDetails.fields.expectedCompleteTime')}
                                            </span>
                                        }
                                        name="expCompleteTime"
                                        rules={[
                                            {required: true, message: t('pickup.pickupTask.pageDetails.messages.pleaseSelectExpectedCompleteTime') || ''},
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();

                                                    const expDepartureTime = form.getFieldValue('expDepartureTime');
                                                    if (!expDepartureTime) return Promise.resolve();

                                                    // 检查是否在expDepartureTime之后
                                                    if (value.isBefore(expDepartureTime)) {
                                                        return Promise.reject(new Error(t('pickup.pickupTask.pageDetails.messages.expectedCompleteTimeMustBeAfterExpectedDepartureTime') || ''));
                                                    }

                                                    // 检查是否在pickupDeadlineTime之前
                                                    const constraints = getTimeConstraints();
                                                    if (constraints?.hubEnd && value.isAfter(constraints.hubEnd.clone().add(1, 'millisecond'))) {
                                                        const errorMessage = constraints.pickupDeadlineTime
                                                            ? t('pickup.pickupTask.pageDetails.messages.deliveryDeadlineMustBeBeforeEndTime', {hubEnd: constraints.hubEnd.format('HH:mm')}) || ''
                                                            : t('pickup.pickupTask.pageDetails.messages.deliveryDeadlineMustBeBeforeEndTime', {hubEnd: constraints.hubEnd.format('HH:mm')}) || '';
                                                        return Promise.reject(new Error(errorMessage));
                                                    }

                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                        className="mb-2"
                                    >
                                        <DatePicker
                                            showTime
                                            format="YYYY/MM/DD HH:mm"
                                            className="w-full"
                                            disabled={isEdit && !getFieldEditability('expCompleteTime')}
                                            disabledDate={(current) => {
                                                const taskDate = form.getFieldValue('taskDate');
                                                if (!taskDate || !current) return false;

                                                // 直接比较日期，不使用时区转换
                                                // 只允许选择taskDate当天
                                                return !current.isSame(taskDate, 'day');
                                            }}
                                            disabledTime={(current) => {
                                                if (!current) return {};
                                                const taskDate = form.getFieldValue('taskDate');
                                                if (!taskDate) return {};

                                                // 如果不是当天，禁用所有时间
                                                if (!current.isSame(taskDate, 'day')) {
                                                    return {
                                                        disabledHours: () => Array.from({length: 24}, (_, i) => i),
                                                        disabledMinutes: () => Array.from({length: 60}, (_, i) => i),
                                                        disabledSeconds: () => Array.from({length: 60}, (_, i) => i)
                                                    };
                                                }

                                                // 如果是当天，根据pickupDeadlineTime限制
                                                const constraints = getTimeConstraints();
                                                if (!constraints) return {};

                                                const {pickupDeadlineTime} = constraints;
                                                if (pickupDeadlineTime) {
                                                    const timeParts = pickupDeadlineTime.split(':').map(Number);
                                                    const deadlineHour = timeParts[0] || 0;
                                                    const deadlineMinute = timeParts[1] || 0;

                                                    return {
                                                        disabledHours: () => {
                                                            const hours = [];
                                                            for (let hour = deadlineHour + 1; hour < 24; hour++) {
                                                                hours.push(hour);
                                                            }
                                                            return hours;
                                                        },
                                                        disabledMinutes: (selectedHour: number) => {
                                                            if (selectedHour === deadlineHour) {
                                                                const minutes = [];
                                                                for (let minute = deadlineMinute + 1; minute < 60; minute++) {
                                                                    minutes.push(minute);
                                                                }
                                                                return minutes;
                                                            }
                                                            return [];
                                                        },
                                                        disabledSeconds: () => []
                                                    };
                                                }

                                                // 如果没有pickup deadline time，允许所有时间（00:00-23:59）
                                                return {};
                                            }}
                                            onChange={() => {
                                                // 当Expected Complete Time改变时，重新验证Expected Departure Time
                                                const expDepartureTime = form.getFieldValue('expDepartureTime');
                                                if (expDepartureTime) {
                                                    form.validateFields(['expDepartureTime']).catch(() => {
                                                        // 验证失败时不需要特殊处理，错误信息会自动显示
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label={<span
                                            className="text-gray-500 whitespace-nowrap">{t('pickup.pickupTask.pageDetails.fields.actualCompleteTime')}</span>}
                                        name="actCompleteTime"
                                        className="mb-2"
                                    >
                                        <DatePicker
                                            showTime
                                            format="YYYY/MM/DD HH:mm"
                                            className="w-full"
                                            disabled
                                        />
                                    </Form.Item>
                                </Col>
                            </>
                        )}
                    </Row>
                </div>
            </Form>

            {/* 统计信息区域 - 与PickupTaskCreateDrawer保持一致的样式 */}
            {isEdit && (
                <div className="bg-gray-50 p-2 mb-4 flex justify-between items-center">
                    <div className="flex space-x-4 text-sm">
                        <div>{t('pickup.pickupTask.pageDetails.fields.appointments')}: <span className="font-medium">{calculateStatistics().appointmentCount}</span>
                        </div>
                        <div>{t('pickup.pickupTask.pageDetails.fields.quantity')}: <span className="font-medium">{calculateStatistics().totalLoad}</span></div>
                        <div>{t('pickup.pickupTask.pageDetails.fields.estimatedDistance')}: <span
                            className="font-medium">{calculateStatistics().estimatedDistance}</span></div>
                        <div>{t('pickup.pickupTask.pageDetails.fields.estimatedDuration')}: <span
                            className="font-medium">{calculateStatistics().estimatedDuration}</span></div>
                    </div>
                </div>
            )}

            {/* 预约详情表格 - 仅在编辑模式下显示 */}
            {isEdit && (
                <div className="mb-6">
                    <Table
                        columns={appointmentColumns}
                        dataSource={appointmentTableData}
                        rowKey={(record) => record.isUnloading ? `unloading_${record.createdAt}` : record.id}
                        pagination={false}
                        size="small"
                        className="mb-4"
                        scroll={{x: 1100}}
                        showHeader={true}
                        loading={tableLoading}
                        rowClassName={(record: any): string => {
                            if (record.status === PickupTaskNodeStatus.COMPLETED) {
                                return 'text-gray-500 bg-gray-50';
                            }
                            if (record.isUnloading) {
                                return 'bg-gray-50';
                            }
                            if (record.isCancelled || record.isTransferred || record.status === 'CANCELLED' || record.status === 'TRANSFER_OUT') {
                                return 'bg-gray-100';
                            }
                            if (record.status === 'PICKUP_FAILED' || record.status === 'PICKED_UP') {
                                return 'text-gray-500 bg-gray-50';
                            }
                            return 'text-sm';
                        }}
                        summary={() => {
                            // 获取终点信息
                            const endPointType = form.getFieldValue('endPointType');
                            const endPointValue = form.getFieldValue('endPointValue');

                            // 根据终点类型获取终点信息
                            const getEndPointInfo = () => {
                                const currentEndPoint = form.getFieldValue('endPoint') || {};
                                const endPointType = currentEndPoint.type;
                                const endPointValue = currentEndPoint.value;

                                if (!endPointType || !endPointValue) {
                                    return {
                                        name: '--',
                                        zipcode: '--',
                                        address: '--'
                                    };
                                }

                                const pointOptions: PickupPointOptionsData = pointOptionsData?.getPickupPointOptions || {
                                    hubList: [],
                                    rallyPointList: [],
                                    firstAppointmentAddress: undefined,
                                    warehouseList: [] // 添加缺失的 warehouseList 字段
                                };

                                // 如果是Hub类型
                                if (endPointType === PickupTaskEndPointType.HUB) {
                                    const selectedHub = pointOptions.hubList.find(hub => hub.value === endPointValue);
                                    if (selectedHub) {
                                        // 从fullAddress中提取邮编（假设格式包含邮编）
                                        const extractZipcode = (address: string): string => {
                                            // 简单的邮编提取逻辑，可以根据实际格式调整
                                            const zipcodeMatch = address.match(/\b\d{5}(-\d{4})?\b/);
                                            return zipcodeMatch ? zipcodeMatch[0] : '--';
                                        };

                                        return {
                                            name: selectedHub.name,
                                            zipcode: selectedHub.fullAddress ? extractZipcode(selectedHub.fullAddress) : '--',
                                            address: selectedHub.fullAddress || selectedHub.name
                                        };
                                    }

                                    return {
                                        name: `Hub ${endPointValue}`,
                                        zipcode: '--',
                                        address: '--'
                                    };
                                }

                                // 如果是中继点类型
                                if (endPointType === PickupTaskEndPointType.RALLY_POINT) {
                                    const selectedRallyPoint = pointOptions.rallyPointList.find(point => point.value === endPointValue);
                                    if (selectedRallyPoint) {
                                        const extractZipcode = (address: string): string => {
                                            const zipcodeMatch = address.match(/\b\d{5}(-\d{4})?\b/);
                                            return zipcodeMatch ? zipcodeMatch[0] : '--';
                                        };

                                        return {
                                            name: selectedRallyPoint.name,
                                            zipcode: selectedRallyPoint.fullAddress ? extractZipcode(selectedRallyPoint.fullAddress) : '--',
                                            address: selectedRallyPoint.fullAddress || selectedRallyPoint.name
                                        };
                                    }

                                    return {
                                        name: `Rally Point ${endPointValue}`,
                                        zipcode: '--',
                                        address: '--'
                                    };
                                }

                                // 其他类型的终点
                                return {
                                    name: 'Destination',
                                    zipcode: '--',
                                    address: '--'
                                };
                            };

                            const endPointInfo = getEndPointInfo();

                            return (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} align="center"></Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="center">
                                        <span className="text-gray-500">Destination</span>
                                    </Table.Summary.Cell>
                                    {/* 揽收站点ID列（Destination 行固定为 -） */}
                                    <Table.Summary.Cell index={2} align="center">
                                        <span className="text-gray-500">-</span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="center">
                                        <span className="text-gray-500">{endPointInfo.zipcode}</span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="center">
                                        <span className="text-gray-500">{endPointInfo.address}</span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="center">-</Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="center">-</Table.Summary.Cell>
                                    <Table.Summary.Cell index={7} align="center">
                                        <span className="text-gray-500">
                                            {endPointInfo.name !== '--' ? '18:00' : '--'}
                                        </span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={8} align="center">-</Table.Summary.Cell>
                                    <Table.Summary.Cell index={9} align="center">-</Table.Summary.Cell>
                                    <Table.Summary.Cell index={10} align="center">-</Table.Summary.Cell>
                                    <Table.Summary.Cell index={11} align="center">-</Table.Summary.Cell>
                                    <Table.Summary.Cell index={12} align="center">-</Table.Summary.Cell>
                                    <Table.Summary.Cell index={13} align="center">-</Table.Summary.Cell>
                                    <Table.Summary.Cell index={14} align="center"></Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                    />
                    {/* +添加按钮独占一行并居中，紧贴表格底部 */}
                    <div
                        className={`w-full flex justify-center items-center py-2 rounded select-none transition ${isTaskReadOnly() || tableLoading || !canOperatePickupTask
                            ? 'bg-gray-100 cursor-not-allowed'
                            : 'bg-purple-50 hover:bg-purple-100 active:bg-purple-200 cursor-pointer'
                        }`}
                        style={{minHeight: 36}}
                        onClick={isTaskReadOnly() || tableLoading || !canOperatePickupTask ? undefined : handleAddStop}
                    >
                        <span
                            className={`text-xs font-medium ${isTaskReadOnly() || tableLoading || !canOperatePickupTask ? 'text-gray-400' : 'text-purple-500'
                            }`}>+ {t('pickup.pickupTask.pageDetails.buttons.add')}</span>
                    </div>
                </div>
            )}

            {/* 交件照片区域 - 仅在编辑模式下显示 */}
            {isEdit && (
                <div className="mb-6">
                    <h3 className="text-base font-medium mb-4">{t('pickup.pickupTask.pageDetails.fields.deliveryPhotos')}</h3>
                    <div className="flex flex-col gap-6">
                        {(() => {
                            const unloadingRecords = appointmentStops
                                .filter((item: any) => item.isUnloading)
                                .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

                            if (unloadingRecords.length === 0) {
                                return (
                                    <div className="text-gray-500 text-sm">
                                        {t('pickup.pickupTask.pageDetails.fields.noDeliveryPhotosAvailable')}
                                    </div>
                                );
                            }

                            return unloadingRecords.map((record: any) => {
                                return (
                                    <div key={record.id ?? `${record.warehouseId}_${record.createdAt}`} className="rounded border border-gray-200 p-4">
                                        <div className="mb-3 flex items-center justify-between gap-4">
                                            <div className="text-sm text-gray-500">
                                                {dayjs(record.createdAt).format('YYYY/MM/DD HH:mm')}
                                            </div>
                                            <div className="text-sm text-gray-700">
                                                {record.site || 'Unknown Hub'}
                                            </div>
                                        </div>
                                        <ImageUploader
                                            value={record.photosS3Keys || []}
                                            displayUrls={record.photosUrls || []}
                                            onChange={(objectKeys) => {
                                                if (record.id) {
                                                    void handleUpdateUnloadRecordPhotos(record.id, objectKeys);
                                                }
                                            }}
                                            maxCount={6}
                                            disabled={!canOperatePickupTask}
                                            tag="pickup-unloading-photo"
                                        />
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>
            )}

            {/* 转到其他任务弹窗 */}
            <TransferTaskModal
                visible={transferModalVisible}
                onClose={() => setTransferModalVisible(false)}
                onConfirm={handleConfirmTransfer}
                currentTaskId={taskId || ''}
                warehouseId={taskDetail?.warehouseId || 0}
                taskDate={taskDetail?.taskDate || ''}
            />

            {/* 添加预约弹窗 */}
            <AddAppointmentModal
                visible={addModalVisible}
                onClose={() => setAddModalVisible(false)}
                onConfirm={handleConfirmAddAppointments}
                currentTaskDate={taskDetail?.taskDate}
            />

            {/* 预约单详情抽屉 */}
            <PickupAppointmentDrawer
                visible={appointmentDrawerVisible}
                onClose={() => {
                    setAppointmentDrawerVisible(false);
                    setSelectedAppointmentId(undefined);
                }}
                mode="detail"
                appointmentId={selectedAppointmentId}
                onSuccess={() => {
                    // 预约单更新后刷新任务详情数据
                    refreshTaskDetail();
                    // 关闭预约单抽屉
                    setAppointmentDrawerVisible(false);
                    setSelectedAppointmentId(undefined);
                }}
            />

            {/* 揽收站点详情抽屉 */}
            <CooperationWarehouseFormDrawer
                visible={cooperationWarehouseDrawerVisible}
                onClose={() => {
                    setCooperationWarehouseDrawerVisible(false);
                    setCooperationWarehouseDrawerMode('view');
                }}
                onSubmit={handleCooperationWarehouseDrawerSubmit}
                initialValues={selectedCooperationWarehouse || undefined}
                mode={cooperationWarehouseDrawerMode}
                statusOptions={[
                    { label: t('enum.cooperationWarehouseStatus.active'), value: 1 },
                    { label: t('enum.cooperationWarehouseStatus.suspended'), value: 0 },
                    { label: t('enum.cooperationWarehouseStatus.pending'), value: 2 },
                ]}
            />

            {/* 揽收站点 2.0：灰度 hub 的站点查看/编辑走 2.0 弹窗（建联状态锁死） */}
            {viewerDrawer}

        </Drawer>
    );
};

export default PickupTaskDetailDrawer; 
